import { formatPhoneNumber, ORDERUP_PRIVACY_POLICY_URL, ORDERUP_TERMS_URL } from '@order-up/common';
import { CHECKOUT_SMS_CONSENT_WORDING } from '@order-up/models';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { isValidCheckoutEmail, isValidCheckoutPhone } from '../lib/customerContact';

export interface CustomerInfo {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface CustomerInformationProps {
    restaurantName: string;
    customerInfo: CustomerInfo;
    onCustomerInfoChange: (info: CustomerInfo) => void;
    promoEmail: boolean;
    promoText: boolean;
    onPromoEmailChange: (value: boolean) => void;
    onPromoTextChange: (value: boolean) => void;
    /** Transactional SMS opt-in: text the order confirmation + tracking link. */
    smsOrderUpdates: boolean;
    onSmsOrderUpdatesChange: (value: boolean) => void;
    /**
     * Customer Loyalty v1 — ENROLLMENT OPT-IN. When true, the "Join rewards"
     * checkbox renders (parent decides whether to show it: only when the
     * loyalty program is active AND the customer isn't already earning). Earning
     * is opt-in — the box is UNCHECKED by default. The customer-facing program
     * name labels the box.
     */
    showLoyaltyOptIn?: boolean;
    loyaltyProgramName?: string;
    joinLoyalty: boolean;
    onJoinLoyaltyChange: (value: boolean) => void;
}

export default function CustomerInformation({
    restaurantName: locationName,
    customerInfo,
    onCustomerInfoChange,
    promoEmail,
    promoText,
    onPromoEmailChange,
    onPromoTextChange,
    smsOrderUpdates,
    onSmsOrderUpdatesChange,
    showLoyaltyOptIn = false,
    loyaltyProgramName,
    joinLoyalty,
    onJoinLoyaltyChange,
}: CustomerInformationProps) {
    // A field says what is wrong with it once the guest has LEFT it — never
    // while they are still typing. Without this the only signal was a greyed
    // "Enter your contact info" button that did not say which field.
    const [touched, setTouched] = useState<Record<'phone' | 'firstName' | 'lastName' | 'email', boolean>>({
        phone: false, firstName: false, lastName: false, email: false,
    });
    const touch = (field: keyof typeof touched) => () =>
        setTouched((t) => (t[field] ? t : { ...t, [field]: true }));
    const errors = {
        phone: touched.phone && !isValidCheckoutPhone(customerInfo.phone)
            ? 'Enter a 10-digit mobile number.' : null,
        firstName: touched.firstName && !customerInfo.firstName.trim() ? 'Enter your first name.' : null,
        lastName: touched.lastName && !customerInfo.lastName.trim() ? 'Enter your last name.' : null,
        email: touched.email && !isValidCheckoutEmail(customerInfo.email)
            ? 'Enter a valid email, like name@example.com.' : null,
    };
    const border = (err: string | null) => (err ? 'border-red-400' : 'border-gray-300');
    const fieldError = (id: string, err: string | null) =>
        err ? <p id={id} className="mt-1.5 text-xs text-red-600" role="alert">{err}</p> : null;

    return (
        <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Your information</h2>
            <div className="space-y-4">
                {/* Mobile Number */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="checkout-phone">
                        Mobile number <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input
                        id="checkout-phone"
                        type="tel"
                        autoComplete="tel-national"
                        required
                        placeholder="(555) 555-5555"
                        value={customerInfo.phone}
                        onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            onCustomerInfoChange({ ...customerInfo, phone: formatted });
                        }}
                        onBlur={touch('phone')}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'checkout-phone-error' : undefined}
                        className={`w-full px-4 py-3 rounded-xl border ${border(errors.phone)} focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder-gray-400`}
                    />
                    {fieldError('checkout-phone-error', errors.phone)}
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="checkout-first-name">
                            First name <span className="text-red-600" aria-hidden="true">*</span>
                        </label>
                        <input
                            id="checkout-first-name"
                            type="text"
                            autoComplete="given-name"
                            required
                            placeholder="First name"
                            value={customerInfo.firstName}
                            onChange={(e) => onCustomerInfoChange({ ...customerInfo, firstName: e.target.value })}
                            onBlur={touch('firstName')}
                            aria-invalid={!!errors.firstName}
                            aria-describedby={errors.firstName ? 'checkout-first-name-error' : undefined}
                            className={`w-full px-4 py-3 rounded-xl border ${border(errors.firstName)} focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder-gray-400`}
                        />
                        {fieldError('checkout-first-name-error', errors.firstName)}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="checkout-last-name">
                            Last name <span className="text-red-600" aria-hidden="true">*</span>
                        </label>
                        <input
                            id="checkout-last-name"
                            type="text"
                            autoComplete="family-name"
                            required
                            placeholder="Last name"
                            value={customerInfo.lastName}
                            onChange={(e) => onCustomerInfoChange({ ...customerInfo, lastName: e.target.value })}
                            onBlur={touch('lastName')}
                            aria-invalid={!!errors.lastName}
                            aria-describedby={errors.lastName ? 'checkout-last-name-error' : undefined}
                            className={`w-full px-4 py-3 rounded-xl border ${border(errors.lastName)} focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder-gray-400`}
                        />
                        {fieldError('checkout-last-name-error', errors.lastName)}
                    </div>
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="checkout-email">
                        Email address <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    {/* Required: the receipt and the link back to this order go
                        here — the one channel that needs no opt-in. See
                        lib/customerContact.ts for why. */}
                    <input
                        id="checkout-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        required
                        aria-describedby={errors.email ? 'checkout-email-error checkout-email-hint' : 'checkout-email-hint'}
                        placeholder="Email address"
                        value={customerInfo.email}
                        onChange={(e) => onCustomerInfoChange({ ...customerInfo, email: e.target.value })}
                        onBlur={touch('email')}
                        aria-invalid={!!errors.email}
                        className={`w-full px-4 py-3 rounded-xl border ${border(errors.email)} focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900 placeholder-gray-400`}
                    />
                    {fieldError('checkout-email-error', errors.email)}
                    <p id="checkout-email-hint" className="mt-1.5 text-xs text-gray-500">
                        We&apos;ll email your receipt and a link to track your order.
                    </p>
                </div>

                {/* Order-update + promo checkboxes */}
                <div className="space-y-3">
                    {/* Customer Loyalty v1 — enrollment opt-in (earning is opt-in;
                        default UNCHECKED). Rendered only when the parent says the
                        program is active and the customer isn't already earning. */}
                    {showLoyaltyOptIn && (
                        <label className="flex items-start gap-3 cursor-pointer">
                            <div
                                className={`shrink-0 w-5 h-5 rounded border-2 transition-all ${joinLoyalty ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                                    }`}
                                onClick={() => onJoinLoyaltyChange(!joinLoyalty)}
                            >
                                {joinLoyalty && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                            </div>
                            <span className="text-sm text-gray-700">
                                Join {loyaltyProgramName || `${locationName} rewards`} and earn points on this order
                            </span>
                        </label>
                    )}
                    {/* Transactional: text me this order's confirmation + tracking. */}
                    <label className="flex items-start gap-3 cursor-pointer">
                        <div
                            className={`shrink-0 w-5 h-5 rounded border-2 transition-all ${smsOrderUpdates ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                                }`}
                            onClick={() => onSmsOrderUpdatesChange(!smsOrderUpdates)}
                        >
                            {smsOrderUpdates && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                        </div>
                        <span className="text-sm text-gray-700">
                            {/* The SAME constant the API stamps into the order's consent record. */}
                            {CHECKOUT_SMS_CONSENT_WORDING}
                        </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                        <div
                            className={`shrink-0 w-5 h-5 rounded border-2 transition-all ${promoEmail ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                                }`}
                            onClick={() => onPromoEmailChange(!promoEmail)}
                        >
                            {promoEmail && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                        </div>
                        <span className="text-sm text-gray-700">
                            Get promotional emails from this restaurant
                        </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                        <div
                            className={`shrink-0 w-5 h-5 rounded border-2 transition-all ${promoText ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-300'
                                }`}
                            onClick={() => onPromoTextChange(!promoText)}
                        >
                            {promoText && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                        </div>
                        <span className="text-sm text-gray-700">
                            Text me marketing offers from OrderUp.
                        </span>
                    </label>
                    {/*
                      A2P 10DLC / TCPA consent disclosure.

                      Carriers vet the opt-in experience against what was
                      submitted with the campaign registration, and the wording
                      below is what that registration describes. Four elements
                      are load-bearing and must not be trimmed: the identified
                      sender, "not a condition of purchase", message frequency +
                      rates, and the STOP/HELP keywords.

                      The brand is the FIFTH, and it is not decoration. 10DLC
                      registers a campaign to ONE brand, and this form may name
                      no other: Sent rejected it on 2026-09-09 for carrying no
                      brand, and again on 2026-09-10 because naming the
                      RESTAURANT made it reference a third-party brand under
                      OrderUp's campaign — "you cannot bundle multiple
                      businesses under one campaign".

                      So no restaurant name appears anywhere in this block, the
                      email line included: the screenshot of this form is filed
                      with the carrier, and a reviewer reads the whole image.
                      Restaurant-branded messaging needs per-restaurant Sent
                      profiles, brands and campaigns — see SMS_SENDER_BRAND. Rendered for BOTH text
                      boxes because either one starts a messaging relationship.
                    */}
                    <p className="text-xs leading-relaxed text-gray-500">
                        You are opting in to receive automated order-update and/or marketing
                        text messages — whichever you selected above — from OrderUp at the
                        number provided. Consent is not a condition of purchase. Msg &amp; data
                        rates may apply. Msg frequency will vary. Reply HELP for help or STOP to
                        opt out. See our{' '}
                        <a
                            href={ORDERUP_PRIVACY_POLICY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-700"
                        >
                            Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a
                            href={ORDERUP_TERMS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-700"
                        >
                            Terms
                        </a>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
}

